/**
 * W&CB Layout Optimizer (Visualizer v2, §F)
 * =========================================
 *
 * Multi-solution panel layout for a single HORIZONTAL W&CB run of length L.
 * Replaces the single waste-minimizing lookup with strategy-driven choices:
 *
 *   - 'fewest-joints' (default) — minimize panel count → joints → H-trims.
 *   - 'least-waste'             — today's behavior (delegates to getOptimalPanels,
 *                                 so it reproduces the current engine exactly).
 *   - 'balanced'                — prefer equal-length panels (visual symmetry),
 *                                 accepting more waste/joints.
 *   - 'lowest-price'            — minimize true dollars using an INJECTED price
 *                                 lookup (active retailer's DB pricing per §N),
 *                                 weighing panels AND per-joint H-trim cost.
 *   - lengthLock                — restrict to a single stock length (transport /
 *                                 retailer stock); overrides strategy.
 *
 * PURE module: pricing arrives via the `panelPrice` callback so this stays
 * testable and retailer-correct (never imports the product table). Scope this
 * milestone: horizontal runs ≥ 8'. Sub-8' runs and vertical orientation keep the
 * existing paths (handled by the caller).
 *
 * Branch-exclusive to `3d-visualizer-v3`.
 */

import { PANEL_LENGTHS } from '../constants';
import { getOptimalPanels } from '../../data/lengthTable';
import { LengthTableEntry } from '../../types';

export type WcbLayoutStrategy = 'fewest-joints' | 'least-waste' | 'balanced' | 'lowest-price';

export interface WcbRunLayout {
  /** Panel stock lengths along one row, descending. */
  lengths: number[];
  count: number;
  /** H-trim joints between panels = count − 1. */
  joints: number;
  /** Bought length minus run length (the last panel's cut-off). */
  wasteFeet: number;
  strategy: WcbLayoutStrategy;
}

export interface WcbOptimizeOptions {
  /** Available stock lengths (default: PANEL_LENGTHS). */
  availableLengths?: number[];
  /** Restrict to one length (overrides strategy). null/undefined = auto. */
  lengthLock?: number | null;
  /** Layout strategy (default: 'fewest-joints'). */
  strategy?: WcbLayoutStrategy;
  /** Price of one full panel of the given stock length — REQUIRED for 'lowest-price'. */
  panelPrice?: (lengthFeet: number) => number;
  /** Marginal cost of one H-trim joint, for 'lowest-price' (default 0). */
  jointCost?: number;
}

/** Expand a length-table entry's per-length counts into a lengths[] array. */
function entryToLengths(entry: LengthTableEntry): number[] {
  const out: number[] = [];
  for (const len of [20, 16, 14, 12, 10, 8]) {
    const n = entry[String(len) as keyof LengthTableEntry] as number;
    for (let i = 0; i < n; i++) out.push(len);
  }
  return out;
}

/**
 * Min-weight multiset of `lengths` whose sum ≥ runLength (and < runLength+maxLen),
 * minimizing Σweight(len), tie-broken by smallest total length (least waste).
 * Unbounded-knapsack DP with reconstruction.
 */
function dpMinWeight(runLength: number, lengths: number[], weight: (len: number) => number): number[] {
  const maxLen = Math.max(...lengths);
  const M = Math.ceil(runLength) + maxLen;
  const best: ({ w: number; pick: number[] } | null)[] = new Array(M + 1).fill(null);
  best[0] = { w: 0, pick: [] };

  for (let s = 1; s <= M; s++) {
    let chosen: { w: number; pick: number[] } | null = null;
    for (const len of lengths) {
      const prev = len <= s ? best[s - len] : null;
      if (prev) {
        const w = prev.w + weight(len);
        if (!chosen || w < chosen.w) chosen = { w, pick: [...prev.pick, len] };
      }
    }
    best[s] = chosen;
  }

  // Smallest sum ≥ runLength achieving the global minimum weight.
  let bestS = -1;
  let bestW = Infinity;
  for (let s = Math.ceil(runLength); s <= M; s++) {
    if (best[s] && best[s]!.w < bestW) {
      bestW = best[s]!.w;
      bestS = s;
    }
  }
  return bestS < 0 ? [] : best[bestS]!.pick;
}

/** Most-balanced layout: a single repeated stock length, min waste then fewer panels. */
function balancedLayout(runLength: number, lengths: number[]): number[] {
  let best: { len: number; n: number; waste: number } | null = null;
  for (const len of lengths) {
    const n = Math.ceil(runLength / len);
    const waste = n * len - runLength;
    if (
      !best ||
      waste < best.waste - 1e-9 ||
      (Math.abs(waste - best.waste) < 1e-9 && n < best.n)
    ) {
      best = { len, n, waste };
    }
  }
  return best ? new Array(best.n).fill(best.len) : [];
}

function makeLayout(lengths: number[], runLength: number, strategy: WcbLayoutStrategy): WcbRunLayout {
  const sorted = [...lengths].sort((a, b) => b - a);
  const sum = sorted.reduce((s, l) => s + l, 0);
  return {
    lengths: sorted,
    count: sorted.length,
    joints: Math.max(0, sorted.length - 1),
    wasteFeet: Math.max(0, sum - runLength),
    strategy,
  };
}

/** Optimize one horizontal W&CB run. */
export function optimizeWcbRun(runLengthFeet: number, opts: WcbOptimizeOptions = {}): WcbRunLayout {
  const strategy = opts.strategy ?? 'fewest-joints';
  if (runLengthFeet <= 0) return { lengths: [], count: 0, joints: 0, wasteFeet: 0, strategy };

  // Length-lock overrides strategy.
  if (opts.lengthLock && opts.lengthLock > 0) {
    const len = opts.lengthLock;
    const n = Math.ceil(runLengthFeet / len);
    return makeLayout(new Array(n).fill(len), runLengthFeet, strategy);
  }

  const available = (opts.availableLengths ?? [...PANEL_LENGTHS]).slice().sort((a, b) => a - b);

  let lengths: number[];
  switch (strategy) {
    case 'least-waste':
      lengths = entryToLengths(getOptimalPanels(Math.ceil(runLengthFeet)));
      break;
    case 'balanced':
      lengths = balancedLayout(runLengthFeet, available);
      break;
    case 'lowest-price':
      lengths = opts.panelPrice
        ? dpMinWeight(runLengthFeet, available, (l) => opts.panelPrice!(l) + (opts.jointCost ?? 0))
        : dpMinWeight(runLengthFeet, available, () => 1); // no prices → fall back to fewest-joints
      break;
    case 'fewest-joints':
    default:
      lengths = dpMinWeight(runLengthFeet, available, () => 1);
      break;
  }

  return makeLayout(lengths, runLengthFeet, strategy);
}
